import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  Optional,
  ViewChild,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { lastValueFrom } from 'rxjs';
import { IChangeSet } from '../changes-dialog/changes-dialog.component';
import { AppStateService } from '../services/app-state.service';
import { AuditService } from '../services/audit.service';
import { AuditObjectIdCountMap, AuditObjectName } from '../services/audit.service.models';
import { StudyTypeService } from '../services/experiment.service';
import { LabpartnerService } from '../services/labpartner.service';
import {
  AuditedChangeResponse,
  AutomatedReport,
  AutomatedReportStatus,
  Bundle,
  Experiment,
  ExperimentType,
  User,
} from '../services/labpartner.service.model';
import {
  EVENT_ARCHIVE_BUNDLE_DIP_REPORT,
  EVENT_ARCHIVE_EXPERIMENT_DIP_REPORT,
  EVENT_UNARCHIVE_BUNDLE_DIP_REPORT,
  EVENT_UNARCHIVE_EXPERIMENT_DIP_REPORT,
} from '../services/logging-constants';
import { LoggingService } from '../services/logging.service';
import { UserAccountService } from '../services/user-account.service';
import { DialogService } from '../shared/dialog.service';
import { NotificationService } from '../shared/notification.service';
import { BaseComponent } from '../support/base.component';
import { FixDuplicateResultsComponent } from './fix-duplicate-results/fix-duplicate-results.component';
import { SelectExternalControlComponent } from './select-external-control/select-external-control.component';

@Component({
  selector: 'app-automated-reports',
  templateUrl: './automated-reports.component.html',
  styleUrls: ['./automated-reports.component.scss'],
  standalone: false,
})
export class AutomatedReportsComponent extends BaseComponent implements AfterViewInit, OnChanges {
  MAXIMUXM_REPORTING_MINUTES = 30;
  TIME_TO_CHECK_CREATED_STATUS_REPORTS = 30000;
  intervalId: any;

  @ViewChild('dipGenerateReportBtn', { read: ElementRef, static: true })
  dipGenerateReportRef!: ElementRef<HTMLButtonElement>;
  @ViewChild(MatPaginator)
  paginator!: MatPaginator;
  automatedReports: AutomatedReport[] = [];

  dataSource: MatTableDataSource<AutomatedReport> = new MatTableDataSource();

  showArchivedAutomatedReports: boolean = false;

  displayedColumns: string[] = [
    'automatedReportId',
    'fileName',
    'createdBy',
    'dateCreated',
    'actions',
    'dailyExternalControls',
    'actions2',
  ];

  currentRoles: string[] = [];
  user: User | undefined = undefined;

  AutomatedReportStatus = AutomatedReportStatus;

  isInDialog: boolean;

  @Input()
  experimentId?: number | null = null;
  experiment: Experiment | null = null;

  bundleId?: number | null = null;
  bundle: Bundle | null = null;

  dailyExternalControlExperimentId: number = 0;

  auditCountMap?: AuditObjectIdCountMap;

  constructor(
    private dialog: MatDialog,
    private labpartnerService: LabpartnerService,
    private accountService: UserAccountService,
    public appState: AppStateService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    private loggingService: LoggingService,
    private auditService: AuditService,
    private studyTypeService: StudyTypeService,
    @Optional() public dialogRef: MatDialogRef<AutomatedReportsComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    super();
    this.isInDialog = !!dialogRef;
    this.bundleId = this.data?.bundleId;
  }

  ngOnChanges(): void {
    this.bundleId = this.data?.bundleId;
    if (this.bundleId) {
      this.loadBundle();
    } else if (this.experimentId) {
      this.loadExperiment();
    }
    this.loadAutomatedReports();
    this.checkCreatedStatusReports();

    this.subscription.add(
      this.accountService.currentUser$.subscribe(user => {
        this.user = user;
      }),
    );
    this.subscription.add(
      this.accountService.currentRoles$.subscribe(roles => {
        this.currentRoles = roles;
      }),
    );

    if (this.bundleId) {
      this.subscription.add(
        this.auditService.bundleAuditCountMap.subscribe(acm => {
          this.auditCountMap = acm.AutomatedReport;
        }),
      );
    } else if (!this.bundleId && this.experimentId) {
      this.subscription.add(
        this.auditService.auditCountMap.subscribe(acm => {
          this.auditCountMap = acm.AutomatedReport;
        }),
      );
    }
  }

  // After the view is initialized, set the paginator
  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  // Clean up the subscription
  protected ngOnDestroyInternal(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  // Load the experiment from the service
  loadExperiment() {
    if (!this.experimentId) {
      return;
    }

    this.subscription.add(
      this.labpartnerService.getExperiment(this.experimentId).subscribe(experiment => {
        this.experiment = experiment;
        this.loadBundle();
      }),
    );
  }

  // Load the bundle from the service
  loadBundle() {
    if (this.bundleId) {
      this.subscription.add(
        this.labpartnerService.getBundleById(this.bundleId).subscribe((bundle: Bundle) => {
          this.bundle = bundle;
          if (bundle.dailyExternalControlExperimentId) {
            this.dailyExternalControlExperimentId = bundle.dailyExternalControlExperimentId;
          }
        }),
      );
    }
  }

  // Load the audit counts from the service
  loadAuditCounts(hideLoader?: boolean) {
    if (this.bundleId) {
      this.subscription.add(
        this.auditService
          .getBundleAuditCounts(this.bundleId, undefined, undefined, hideLoader)
          .subscribe(() => {}),
      );
    } else if (this.experimentId) {
      this.subscription.add(
        this.auditService
          .getAuditCounts(this.experimentId, undefined, undefined, hideLoader)
          .subscribe(() => {}),
      );
    }
  }

  // Check every 30 seconds if there are any automatedReports with the status Created
  checkCreatedStatusReports() {
    this.intervalId = setInterval(() => {
      console.log('Checking for created status reports');
      const hasCreatedStatusReport = this.automatedReports.some(
        report => report.status === AutomatedReportStatus.Created,
      );
      if (hasCreatedStatusReport) {
        this.loadAutomatedReports(true);
      }
    }, this.TIME_TO_CHECK_CREATED_STATUS_REPORTS);
  }

  // Load the automatedReports from the service
  async loadAutomatedReports(hideLoader?: boolean) {
    this.loadAuditCounts(hideLoader);

    await this.accountService.SetUserList(true);
    // If the bundleId is set, get the automatedReports for that bundle
    if (this.bundleId) {
      this.subscription.add(
        this.labpartnerService
          .getAutomatedReportsByBundleId(this.bundleId, hideLoader)
          .subscribe(automatedReports => {
            automatedReports.forEach(automatedReport => {
              if (automatedReport.createdBy) {
                Object.assign(automatedReport, {
                  createdByName: this.accountService.getUserName(automatedReport.createdBy),
                });
              }
            });
            this.automatedReports = automatedReports;
            this.setAutomatedReports();
          }),
      );
    }
    // If the experimentId is set, get the automatedReports for that experiment
    else if (this.experimentId) {
      this.subscription.add(
        this.labpartnerService
          .getAutomatedReportsByExperimentId(this.experimentId, hideLoader)
          .subscribe(automatedReports => {
            automatedReports.forEach(automatedReport => {
              if (automatedReport.createdBy) {
                Object.assign(automatedReport, {
                  createdByName: this.accountService.getUserName(automatedReport.createdBy),
                });
              }
            });
            this.automatedReports = automatedReports;
            this.setAutomatedReports();
          }),
      );
    }
  }

  // Filter the automatedReports based on the showArchivedAutomatedReports flag
  setAutomatedReports() {
    if (this.showArchivedAutomatedReports) {
      this.dataSource.data = this.automatedReports;
    } else {
      this.dataSource.data = this.automatedReports.filter(
        automatedReport => automatedReport.isActive == true,
      );
    }

    this.dataSource._updateChangeSubscription();
  }

  // Add an automatedReport
  addAutomatedReport() {
    this.dipGenerateReportRef.nativeElement.blur();

    if (this.bundleId) {
      this.addAutomatedReportForBundle();
    } else if (this.experimentId) {
      this.addAutomatedReportForExperiment();
    }
  }

  // Add an automatedReport for an experiment
  async addAutomatedReportForExperiment() {
    if (!this.experimentId) {
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '90%';
    dialogConfig.height = '95%';
    // Open the SelectExternalControlComponent dialog
    const selectExternalControlResult = await lastValueFrom(
      this.dialog.open(SelectExternalControlComponent, dialogConfig).afterClosed(),
    );

    // If the dialog was cancelled, return
    if (!selectExternalControlResult || selectExternalControlResult.ok == false) {
      return;
    }

    this.dailyExternalControlExperimentId = selectExternalControlResult.data.experimentId;

    const userSelection = await this.resolveDuplicatedResults([
      this.experimentId,
      this.dailyExternalControlExperimentId,
    ]);

    if (userSelection == null) {
      return;
    }

    const automatedReport: AutomatedReport = {
      experimentId: this.experimentId,
      studyType: this.experiment?.studyType,
      hasDuplicateResults: userSelection.length > 0,
      dailyExternalControlExperimentId: this.dailyExternalControlExperimentId,
      userSelectedResults: userSelection.map((x: any) => {
        return {
          sampleId: x.sampleId,
          testResultId: x.testResultId,
        };
      }),
    };

    this.subscription.add(
      this.labpartnerService.addAutomatedReport(automatedReport).subscribe(
        () => {
          this.notificationService.success(':: Data Insights Preview created successfully');
          this.loadAutomatedReports();
        },
        () => {
          this.notificationService.error(':: Error creating Data Insights Preview');
        },
      ),
    );
  }

  // Add an automatedReport for a bundle
  async addAutomatedReportForBundle() {
    if (!this.bundleId) {
      return;
    }
    if (this.bundle?.bundleStudyType == null) {
      return;
    }

    const userSelection = await this.resolveDuplicatedResults(this.bundle.experimentIds.split(','));

    if (userSelection == null) {
      return;
    }

    const automatedReport: AutomatedReport = {
      bundleId: this.bundle?.bundleId,
      studyType: this.bundle?.bundleStudyType,
      hasDuplicateResults: userSelection.length > 0,
      dailyExternalControlExperimentId: this.dailyExternalControlExperimentId,
      userSelectedResults: userSelection.map((x: any) => {
        return {
          sampleId: x.sampleId,
          testResultId: x.testResultId,
        };
      }),
    };

    this.subscription.add(
      this.labpartnerService.addAutomatedReport(automatedReport).subscribe(
        () => {
          this.notificationService.success(':: Data Insights Preview created successfully');
          this.loadAutomatedReports();
        },
        () => {
          this.notificationService.error(':: Error creating Data Insights Preview');
        },
      ),
    );
  }

  // Archive an automatedReport
  archiveAutomatedReport(automatedReport: AutomatedReport) {
    if (!automatedReport.automatedReportId) {
      return;
    }

    // Create a changeSet for the changes dialog
    const changeSet: IChangeSet[] = [];
    changeSet.push({
      identifier: `DIP report ${automatedReport.automatedReportId}`,
      field: 'DIP report',
      oldValue: `UNARCHIVED`,
      newValue: 'ARCHIVED',
    });

    // Open the changes dialog
    const dialogRef = this.dialogService.openConfirmChangesDialog(
      changeSet,
      this.bundleId || this.experiment == null || this.experiment.type === null
        ? ExperimentType.ResearchAndDevelopment
        : this.experiment.type,
      { isDelete: false },
    );

    // Subscribe to the dialog result
    this.subscription.add(
      dialogRef.afterClosed().subscribe(async data => {
        if (
          data?.submitClicked &&
          (data?.reasonProvided ||
            this.bundleId ||
            this.experiment == null ||
            this.experiment.type == ExperimentType.ResearchAndDevelopment ||
            this.experiment.type === null)
        ) {
          if (!automatedReport.automatedReportId) {
            return;
          }

          this.subscription.add(
            this.labpartnerService
              .archiveAutomatedReport(automatedReport.automatedReportId, data.batchReason)
              .subscribe(
                () => {
                  if (!automatedReport.automatedReportId) {
                    return;
                  }

                  this.loadAutomatedReports();
                  this.notificationService.success('DIP report archived');

                  // Log the event
                  let props: { [key: string]: string | number } = {};
                  if (this.bundleId) {
                    props = {
                      BundleId: this.bundleId,
                      DIPReportID: automatedReport.automatedReportId,
                      ExperimentOwner: this.bundle?.createdBy ?? 0,
                    };
                    this.loggingService.logEvent(EVENT_ARCHIVE_BUNDLE_DIP_REPORT, props);
                  } else if (this.experimentId && this.experiment) {
                    props = {
                      ExperimentId: this.experimentId,
                      DIPReportID: automatedReport.automatedReportId,
                      DeviceType: this.experiment?.deviceType,
                      ExperimentOwner: this.experiment?.createdBy ?? 0,
                    };
                    this.loggingService.logEvent(EVENT_ARCHIVE_EXPERIMENT_DIP_REPORT, props);
                  }
                },
                (err: any) => {
                  const expectedErr = err.error as AuditedChangeResponse;
                  if (expectedErr.expectedException) {
                    this.notificationService.error(expectedErr.message);
                  } else {
                    throw err;
                  }
                },
              ),
          );
        }
      }),
    );
  }

  // Unarchive an automatedReport
  unarchiveAutomatedReport(automatedReport: AutomatedReport) {
    if (!automatedReport.automatedReportId) {
      return;
    }

    // Create a changeSet for the changes dialog
    const changeSet: IChangeSet[] = [];
    changeSet.push({
      identifier: `DIP report ${automatedReport.automatedReportId}`,
      field: 'DIP report',
      oldValue: `ARCHIVED`,
      newValue: 'UNARCHIVED',
    });

    // Open the changes dialog
    const dialogRef = this.dialogService.openConfirmChangesDialog(
      changeSet,
      this.bundleId || this.experiment == null || this.experiment.type === null
        ? ExperimentType.ResearchAndDevelopment
        : this.experiment.type,
      { isDelete: false },
    );

    // Subscribe to the dialog result
    this.subscription.add(
      dialogRef.afterClosed().subscribe(async data => {
        if (
          data?.submitClicked &&
          (data?.reasonProvided ||
            this.bundleId ||
            this.experiment == null ||
            this.experiment.type == ExperimentType.ResearchAndDevelopment ||
            this.experiment.type === null)
        ) {
          if (!automatedReport.automatedReportId) {
            return;
          }

          this.subscription.add(
            this.labpartnerService
              .unarchiveAutomatedReport(automatedReport.automatedReportId, data.batchReason)
              .subscribe(
                () => {
                  if (!automatedReport.automatedReportId) {
                    return;
                  }

                  this.loadAutomatedReports();
                  this.notificationService.success('DIP report unarchived');

                  let props: { [key: string]: string | number } = {};
                  if (this.bundleId) {
                    props = {
                      BundleId: this.bundleId,
                      DIPReportID: automatedReport.automatedReportId,
                      ExperimentOwner: this.bundle?.createdBy ?? 0,
                    };
                    this.loggingService.logEvent(EVENT_UNARCHIVE_BUNDLE_DIP_REPORT, props);
                  } else if (this.experimentId && this.experiment) {
                    props = {
                      ExperimentId: this.experimentId,
                      DIPReportID: automatedReport.automatedReportId,
                      DeviceType: this.experiment?.deviceType,
                      ExperimentOwner: this.experiment?.createdBy ?? 0,
                    };
                    this.loggingService.logEvent(EVENT_UNARCHIVE_EXPERIMENT_DIP_REPORT, props);
                  }
                },
                (err: any) => {
                  const expectedErr = err.error as AuditedChangeResponse;
                  if (expectedErr.expectedException) {
                    this.notificationService.error(expectedErr.message);
                  } else {
                    throw err;
                  }
                },
              ),
          );
        }
      }),
    );
  }

  // Download an automatedReport
  downloadAutomatedReport(row: AutomatedReport) {
    if (row.automatedReportId == null) {
      return;
    }

    this.labpartnerService.downloadAutomatedReport(row.automatedReportId).subscribe(response => {
      if (!response.body) {
        return;
      }

      // Extract the filename from the Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      console.log(contentDisposition);
      let filename = 'default-filename'; // Fallback filename

      if (contentDisposition) {
        const matches = /filename=([^;]+)/.exec(contentDisposition);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      // Create a Blob URL and trigger the download
      const url = window.URL.createObjectURL(response.body);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename; // Use the extracted filename
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }

  // Get the filename for the automatedReport
  getFileName(row: AutomatedReport) {
    if (row.bundleId) {
      return this.studyTypeService.getStudyTypeConfig(row.studyType)?.bundleDIPFileName ?? '';
    } else if (row.experimentId) {
      return this.studyTypeService.getStudyTypeConfig(row.studyType)?.experimentDIPFileName ?? '';
    }
    return '';
  }

  // Resolve duplicated results
  private async resolveDuplicatedResults(experimentIds: any[]) {
    const userSelection: any[] = [];
    for (const experimentId of experimentIds) {
      const dialogResult = await lastValueFrom(
        this.dialog
          .open(FixDuplicateResultsComponent, {
            hasBackdrop: true,
            backdropClass: 'dynamic-dialog-backdrop',
            data: {
              experimentId: experimentId,
            },
          })
          .afterClosed(),
      );

      if (!dialogResult) {
        this.notificationService.warn(':: Cancelled');
        return null;
      }
      if (!dialogResult.status) {
        return null;
      }

      userSelection.push(
        ...dialogResult.userSelection.map((x: any) => {
          return {
            sampleId: x.sampleId,
            testResultId: x.testResultId,
          };
        }),
      );
    }
    return userSelection;
  }

  // Check if the report was created within the last 30 minutes
  public itsReportOntime(automatedReport: AutomatedReport) {
    if (!automatedReport.dateCreated) {
      return false;
    }
    const dateCreated = new Date(automatedReport.dateCreated);
    const currentDate = new Date();
    const diff = Math.abs(currentDate.getTime() - dateCreated.getTime());
    const diffMinutes = Math.floor(diff / 1000 / 60);
    return diffMinutes <= this.MAXIMUXM_REPORTING_MINUTES;
  }

  showColumnAuditDialog(row: AutomatedReport) {
    if (!row.automatedReportId) {
      return;
    }

    if (this.bundleId) {
      this.subscription.add(
        this.dialogService.openBundleChangelogDialog(
          `Change log for DIP report`,
          this.bundleId,
          AuditObjectName.AutomatedReport,
          row.automatedReportId,
          undefined,
          false,
        ),
      );
    } else if (this.experimentId) {
      this.subscription.add(
        this.dialogService.openChangelogDialog(
          `Change log for DIP report`,
          this.experimentId,
          AuditObjectName.AutomatedReport,
          row.automatedReportId,
          undefined,
          false,
        ),
      );
    }
  }
}
